/**
 * The field bank: reusable quote-card fields that any product layout can pick
 * from.
 *
 * Each export takes the label to print and returns a QuoteCardField. The label
 * is an argument because the same column is named differently on each carrier's
 * illustration ("Specified Amount" on CareMatters II is "Death Benefit"
 * elsewhere), and the card is meant to use the illustration's wording.
 *
 * A field that has nothing to show returns null and the row is dropped. The one
 * exception is electedPremium, which prints "Not Elected" the way the
 * illustration does. Fields that only one product needs live in that product's
 * own file instead of here.
 */

import { getBenefitPaymentCapabilities } from '../benefitPaymentCapabilities';
import { formatCurrency } from '../formatCurrency';
import type { QuoteCardField, QuoteCardQuote } from './types';

type Pick = (quote: QuoteCardQuote) => unknown;

const NOT_ELECTED = 'Not Elected';

const dollars = (amount: number) => formatCurrency(Math.ceil(amount));

function toAmount(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

export function formatBenefitPaymentType(quote: { carrierName?: string | null }): string {
  const capabilities = getBenefitPaymentCapabilities(quote);
  const labelOf = (mode: string) => (mode === 'reimbursement' ? 'Reimbursement' : 'Indemnity');
  const otherModes = capabilities.supports.filter(mode => mode !== capabilities.default);
  if (otherModes.length === 0) return labelOf(capabilities.default);
  return `${labelOf(capabilities.default)} (${labelOf(otherModes[0])} also available)`;
}

export const currency =
  (label: string, pick: Pick): QuoteCardField =>
  quote => {
    const amount = toAmount(pick(quote));
    return amount === null ? null : { label, value: dollars(amount) };
  };

export const monthlyBenefit =
  (label: string): QuoteCardField =>
  quote => ({ label, value: `${dollars(quote.ltcBenefitMonthlyIndividual)}/month` });

export const benefitPeriod =
  (label: string, unit: 'years' | 'months'): QuoteCardField =>
  quote => {
    const months = quote.ltcBenefitDurationMonths;
    if (!months) return null;
    const years = months / 12;
    const value = unit === 'months' ? `${months} months` : `${years} year${years === 1 ? '' : 's'}`;
    return { label, value, testId: 'quote-detail-benefit-duration' };
  };

export const premium =
  (label: string): QuoteCardField =>
  quote => ({
    label,
    value: formatCurrency(Math.round(quote.premiumAmount)),
    testId: 'quote-detail-premium',
  });

export const electedPremium =
  (label: string, pick: Pick): QuoteCardField =>
  quote => {
    const amount = toAmount(pick(quote));
    return { label, value: amount === null ? NOT_ELECTED : dollars(amount) };
  };

export const inflationOption =
  (label: string): QuoteCardField =>
  quote =>
    quote.inflationBenefitOption
      ? {
          label,
          value: quote.inflationBenefitOption,
          testId: 'quote-detail-inflation-protection',
        }
      : null;

export const benefitType =
  (label: string): QuoteCardField =>
  quote => ({
    label,
    value: formatBenefitPaymentType(quote),
    testId: 'quote-detail-benefit-type',
  });

export const tobaccoClass =
  (label: string): QuoteCardField =>
  quote => ({ label, value: quote.primaryTobaccoStatus });

export const fixed =
  (label: string, value: string): QuoteCardField =>
  () => ({ label, value });
